import { useEffect, useState } from 'react';
import { useStore } from '../state/store';
import { TopBar } from '../components/TopBar';
import { Banner } from '../ui';
import { getStorage } from '../db/storage';

interface Props {
  onBack: () => void;
}

/**
 * Privacy reassurance page reachable from the top-bar lock pill, the
 * dismissable PrivacyBanner, and the Settings list. Friendly tone — no
 * technical scare-words. Includes a "Show me what's stored" panel that
 * dumps the active profile JSON read-only so users can verify with their
 * own eyes that nothing surprising lives in there.
 */
export function PrivacyPage({ onBack }: Props) {
  const { activeProfile, exportJson } = useStore();
  const [backendKind, setBackendKind] = useState<'file' | 'indexeddb' | 'unknown'>('unknown');
  const [showJson, setShowJson] = useState(false);
  const [json, setJson] = useState<string | null>(null);
  const [loadingJson, setLoadingJson] = useState(false);
  const [copyMsg, setCopyMsg] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getStorage().then(s => {
      if (!cancelled) setBackendKind(s.kind);
    }).catch(() => {
      /* leave as unknown */
    });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleShowJson() {
    setLoadingJson(true);
    try {
      const j = await exportJson();
      setJson(j);
      setShowJson(true);
    } finally {
      setLoadingJson(false);
    }
  }

  async function handleCopy() {
    if (!json) return;
    try {
      await navigator.clipboard.writeText(json);
      setCopyMsg('Copied to clipboard.');
      setTimeout(() => setCopyMsg(null), 2000);
    } catch {
      setCopyMsg('Copy failed — select and copy manually.');
      setTimeout(() => setCopyMsg(null), 3000);
    }
  }

  const storageDescription =
    backendKind === 'file'
      ? `Dev mode — your data lives in the local file local-data/profiles/${activeProfile}.json on this machine.`
      : backendKind === 'indexeddb'
        ? `Production build — your data lives in this browser's IndexedDB (database: cairn, profile: "${activeProfile}").`
        : 'Detecting storage backend…';

  return (
    <>
      <TopBar
        title="Privacy"
        subtitle="Where your data lives, and how we keep it here"
        right={
          <button className="btn btn-ghost text-sm" onClick={onBack}>
            Back
          </button>
        }
      />
      <main className="mx-auto max-w-3xl space-y-4 px-4 py-4 pb-nav">
        <Banner tone="success" icon="🔒" title="All your data lives on this device.">
          Cairn was built so your finances never have to leave your machine. We can't see them
          even if we wanted to.
        </Banner>

        <section className="card card-dark p-4 space-y-3">
          <h3 className="text-sm font-semibold">Where it's stored</h3>
          <ul className="space-y-2 text-xs text-ink-600 dark:text-ink-300">
            <li className="flex gap-2">
              <span aria-hidden>•</span>
              <span>
                <strong>Dev mode</strong> (running <code>npm run dev</code>): plain JSON files at
                {' '}<code>local-data/profiles/&lt;name&gt;.json</code>. Gitignored. You can open
                them in any editor.
              </span>
            </li>
            <li className="flex gap-2">
              <span aria-hidden>•</span>
              <span>
                <strong>Production / static build</strong>: your browser's IndexedDB. Bound to this
                origin and this browser profile — clearing browser data wipes it.
              </span>
            </li>
          </ul>
        </section>

        <section className="card card-dark p-4 space-y-3">
          <h3 className="text-sm font-semibold">Zero network calls at runtime</h3>
          <p className="text-xs text-ink-600 dark:text-ink-300">
            The Content Security Policy in <code>index.html</code> enforces this — no
            {' '}<code>connect-src</code> to any host except (in dev) the local Vite server.
            Verify it yourself in DevTools → Network: load any page, take any action. The Network
            panel stays empty.
          </p>
          <p className="text-xs text-ink-600 dark:text-ink-300">
            No analytics. No telemetry. No fonts loaded from a CDN. No third-party scripts.
          </p>
        </section>

        <section className="card card-dark p-4 space-y-3">
          <h3 className="text-sm font-semibold">Verify privacy</h3>
          <div className="rounded-xl border border-ink-200 bg-ink-50/60 p-3 text-xs dark:border-ink-700 dark:bg-ink-800/40">
            <div className="text-ink-500 dark:text-ink-300">Active profile</div>
            <div className="mt-0.5 font-mono text-sm font-semibold text-ink-900 dark:text-ink-50">
              {activeProfile}
            </div>
            <div className="mt-2 text-ink-500 dark:text-ink-300">Storage backend</div>
            <div className="mt-0.5 text-sm text-ink-700 dark:text-ink-100">{storageDescription}</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleShowJson}
              disabled={loadingJson}
              className="btn btn-primary text-xs"
            >
              {loadingJson ? 'Loading…' : showJson ? 'Refresh stored JSON' : "Show me what's stored"}
            </button>
            {showJson && json && (
              <button type="button" onClick={handleCopy} className="btn btn-ghost text-xs">
                Copy to clipboard
              </button>
            )}
            {showJson && (
              <button
                type="button"
                onClick={() => { setShowJson(false); setJson(null); }}
                className="btn btn-ghost text-xs"
              >
                Hide
              </button>
            )}
          </div>
          {copyMsg && (
            <div className="text-[11px] text-ink-500 dark:text-ink-300">{copyMsg}</div>
          )}
          {showJson && json && (
            <pre
              className="max-h-96 overflow-auto rounded-xl border border-ink-200 bg-ink-50 p-3 font-mono text-[11px] leading-relaxed text-ink-800 dark:border-ink-700 dark:bg-ink-900 dark:text-ink-200"
              aria-label="Active profile JSON (read-only)"
            >
              {json}
            </pre>
          )}
        </section>

        <section className="card card-dark p-4 space-y-2">
          <h3 className="text-sm font-semibold">A note on the demo profile</h3>
          <p className="text-xs text-ink-600 dark:text-ink-300">
            The "demo" profile ships with deliberately fictional sample data — different person,
            different ages, different scenarios. It's safe for screenshots and demos. Your own
            data lives in the other profiles you create.
          </p>
        </section>
      </main>
    </>
  );
}
